import _ from "lodash";
import ko from "knockout";
import userService from "services/userService";
import LOG from "services/loggingService";
import ViewModel from "viewModels/ViewModel";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

/**
 * The AgentSelectorView class.
 * @class
 */
const AgentSelectorView = ViewModel.extend(function (
    agentsObservable,
    options
) {
    const self = this;

    self.agents = agentsObservable;
    self.placeholderText = ko.i18n.renderString(
        "agentSelectorView.placeholderText"
    );
    self.qualityEvaluatorPlaceholderText = ko.i18n.renderString(
        "agentSelectorView.qualityEvaluator.placeholderText"
    );
    self.qualityAdminPlaceholderText = ko.i18n.renderString(
        "agentSelectorView.qualityAdmin.placeholderText"
    );
    self.auditUserPlaceholderText = ko.i18n.renderString(
        "agentSelectorView.auditUser.placeholderText"
    );
    self.isOpen = ko.observable(false);
    self.isLookingUpUsers = ko.observable(false);
    self.filteredAgents = ko.observableArray([]);

    const isSparkFlareFTEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
    );

    const lookupUsers = function (searchTerm) {
        const query = [];

        if (options && options.userStates) {
            query.push({
                fields: ["state"],
                values: options.userStates,
                type: "EXACT",
                operator: "OR",
            });
        }

        if (searchTerm && searchTerm.length > 0) {
            query.push({
                fields: ["name"],
                value: searchTerm.toLowerCase(),
                type: "QUERY_STRING",
                operator: "AND",
            });
        }

        const queryObject = {
            body: {
                pageSize: 10,
                types: ["users"],
                query: query,
            },
        };

        return userService
            .searchUsers(queryObject)
            .then(function (response) {
                let filteredAgents = ko.toJS(response);
                const unwrappedAgents = ko.unwrap(self.agents);

                if (!isSparkFlareFTEnabled) {
                    // Remove the already-selected agents from the list
                    _.remove(filteredAgents, function (agent) {
                        return _.find(unwrappedAgents, { id: agent.id });
                    });
                }

                //Removes ignored users if specified.
                if (options && options.ignoredIds) {
                    _.remove(filteredAgents, function (agent) {
                        return _.includes(options.ignoredIds(), agent.id);
                    });
                }

                if (isSparkFlareFTEnabled && _.isArray(unwrappedAgents)) {
                    filteredAgents = _.unionBy(
                        filteredAgents,
                        unwrappedAgents,
                        "id"
                    );
                }

                self.filteredAgents(filteredAgents);
                return filteredAgents;
            })
            .catch(function (error) {
                LOG.error("Couldn't retrieve users with the supplied name", {
                    error: error,
                });
                self.filteredAgents([]);
                return [];
            })
            .finally(function () {
                self.isLookingUpUsers(false);
            });
    };

    const debouncedLookupUsers = _.debounce(lookupUsers, 500);

    self.getQualityEvaluators = function (searchTerm) {
        LOG.info(
            "AgentSelectorView.getQualityEvaluators is deprecated due to roles being more fluid."
        );
        return lookupUsers(searchTerm, "qualityEvaluator");
    };

    self.getQualityAdmins = function (searchTerm) {
        LOG.info(
            "AgentSelectorView.getQualityAdmins is deprecated due to roles being more fluid."
        );
        return lookupUsers(searchTerm, "qualityAdmin");
    };

    self.getAgents = function (searchTerm) {
        return lookupUsers(searchTerm);
    };

    self.configInitialAgents = function (element) {
        if (!element) {
            return;
        }

        const unwrappedAgents = ko.unwrap(self.agents);
        self.filteredAgents(unwrappedAgents);

        if (_.isArray(unwrappedAgents)) {
            const selectedAgentIds = unwrappedAgents.map((agent) => agent.id);
            element["value"] = selectedAgentIds.join();
        } else {
            element["value"] = unwrappedAgents?.id;
        }
    };

    self.filterAgents = function (_data, event) {
        self.isLookingUpUsers(true);
        debouncedLookupUsers(event?.detail);
    };

    self.onSingleSelectedAgentChange = function (_data, event) {
        const selectedAgentId = event?.target?.value;
        const selectedAgent = self
            .filteredAgents()
            .find((agent) => agent.id === selectedAgentId);
        self.agents(selectedAgent);
    };

    self.onMultipleSelectedAgentsChange = function (_data, event) {
        event?.target?.getSelectedValues()?.then((selectedAgentIDs) => {
            const selectedAgents = self
                .filteredAgents()
                .filter((agent) => selectedAgentIDs.includes(agent?.id));
            self.agents.remove(
                (agent) => !selectedAgentIDs.includes(agent?.id)
            );
            self.agents(_.unionBy(self.agents(), selectedAgents, "id"));
        });
    };

    self.assignCurrentUserAsEvaluator = function () {
        self.agents(userService.currentUser());
    };

    self.isLoggedInUserPresent = self.computed(function () {
        return userService.currentUser();
    });

    if (options && options.agentValidation) {
        self.agents.extend(options.agentValidation);
    } else {
        if (_.isArray(self.agents())) {
            self.agents.extend({
                deepComparer: true,
                minLength: {
                    params: 1,
                    message: "agentSelectorView.noAgentsSelected",
                },
            });
        } else {
            self.agents.extend({ required: true });
        }
    }

    self.onDispose = function () {
        if (debouncedLookupUsers?.cancel) {
            debouncedLookupUsers.cancel();
        }
    };
},
true /* pass args to extended class */);

export default AgentSelectorView;
